import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { isIOS, isStandalone } from "./registerSW";
import { useI18n } from "../lib/i18n";

let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; window.dispatchEvent(new Event("pm-install-available")); });
  window.addEventListener("appinstalled", () => { deferredPrompt = null; localStorage.setItem("pm_pwa_installed", "1"); window.dispatchEvent(new Event("pm-install-done")); });
}

const key = (userId) => `pm_install_dismissed_${userId || "anon"}`;

/** Android: native install prompt. iPhone: Share → Add to Home Screen hint. Remembered per user; hidden when installed. */
export function InstallPrompt({ userId, lang: langProp, className = "" }) {
  const { lang: ctxLang } = useI18n();
  const lang = langProp || ctxLang;
  const [canPrompt, setCanPrompt] = useState(!!deferredPrompt);
  const [hidden, setHidden] = useState(() => isStandalone() || localStorage.getItem("pm_pwa_installed") === "1" || localStorage.getItem(key(userId)) === "1");
  useEffect(() => {
    const show = () => setCanPrompt(true);
    const done = () => setHidden(true);
    window.addEventListener("pm-install-available", show);
    window.addEventListener("pm-install-done", done);
    return () => { window.removeEventListener("pm-install-available", show); window.removeEventListener("pm-install-done", done); };
  }, []);
  useEffect(() => { setHidden(isStandalone() || localStorage.getItem("pm_pwa_installed") === "1" || localStorage.getItem(key(userId)) === "1"); }, [userId]);

  const ios = isIOS();
  if (hidden || (!canPrompt && !ios)) return null;
  const dismiss = () => { localStorage.setItem(key(userId), "1"); setHidden(true); };
  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    if (outcome === "accepted") setHidden(true); else dismiss();
  };
  const en = lang === "en";

  return (
    <div className={`relative rounded-2xl bg-[#0A0F0C] text-white p-4 pr-11 flex items-center gap-3 ring-1 ring-white/10 ${className}`} data-testid="pwa-install-banner">
      <img src="/icons/icon-192.png" alt="" className="w-11 h-11 rounded-xl shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold leading-tight">{en ? "Install Playmaker" : "Installeer Playmaker"}</div>
        {canPrompt ? (
          <div className="text-xs text-white/60 mt-0.5">{en ? "Faster access from your home screen, with notifications." : "Sneller openen vanaf je beginscherm, met meldingen."}</div>
        ) : (
          <div className="text-xs text-white/70 mt-0.5 flex items-center gap-1 flex-wrap" data-testid="pwa-ios-hint">
            {en ? "Tap" : "Tik op"} <Share size={13} className="inline text-emerald-400" /> <b>{en ? "Share" : "Deel"}</b> → <b>{en ? "Add to Home Screen" : "Zet op beginscherm"}</b>
          </div>
        )}
      </div>
      {canPrompt && (
        <button type="button" onClick={install} className="h-10 px-4 rounded-full bg-emerald-400 text-[#0A0F0C] text-sm font-bold flex items-center gap-1.5 shrink-0 active:scale-[.98]" data-testid="pwa-install-btn">
          <Download size={14} /> {en ? "Install" : "Installeer"}
        </button>
      )}
      <button type="button" onClick={dismiss} aria-label="Sluiten" className="absolute top-2 right-2 w-9 h-9 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10" data-testid="pwa-install-dismiss"><X size={16} /></button>
    </div>
  );
}
